import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { fetchMe } from '../lib/meClient';

const AUTH_PATH_PREFIXES = ['/login', '/register', '/auth/', '/pending-approval'];

function isAuthPath(pathname: string): boolean {
  return AUTH_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix));
}

function instanceSlugFromPath(pathname: string): string | null {
  return pathname.match(/^\/i\/([^/]+)/)?.[1] ?? null;
}

/** Tie errors to the logged-in user when /api/me succeeds. Re-runs after login and instance switch. */
export function SentryIdentifyUser() {
  const location = useLocation();
  const instanceSlug = instanceSlugFromPath(location.pathname);
  const onAuthPath = isAuthPath(location.pathname);

  useEffect(() => {
    if (onAuthPath) return;

    let cancelled = false;
    fetchMe()
      .then((data) => {
        if (cancelled || !data?.user?.id) return;
        Sentry.setUser({
          id: data.user.id,
          email: data.user.email,
          username: data.user.username,
        });
        if (data.activeInstance?.id) {
          Sentry.setTag('instance_id', data.activeInstance.id);
        } else {
          Sentry.setTag('instance_id', '');
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [instanceSlug, onAuthPath]);

  return null;
}

function SentryErrorFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-app-surface-muted p-6">
      <div className="max-w-md rounded-lg border border-app-border bg-app-surface p-6 text-center shadow-sm">
        <h1 className="text-lg font-semibold text-app-text">Etwas ist schiefgelaufen</h1>
        <p className="mt-2 text-sm text-app-text-muted">
          Ein unerwarteter Fehler ist aufgetreten. Bitte laden Sie die Seite neu.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          Seite neu laden
        </button>
      </div>
    </div>
  );
}

export function SentryAppBoundary({ children }: { children: ReactNode }) {
  return <Sentry.ErrorBoundary fallback={<SentryErrorFallback />}>{children}</Sentry.ErrorBoundary>;
}
